'use memo';

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { DeviceCatalogInfo } from '@/components/Combobox/DeviceCatalogCombobox';
import Icon from '@/components/Icon/Icon';
import { DeviceCatalogSelect } from '@/components/Select/DeviceCatalogSelect';
import { Text } from '@/components/Typography/Text';
import type { DeviceGenre } from '@/constants/device';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
import { buildDeviceCatalogReadView } from './buildDeviceCatalogReadView';
import { buildDeviceCatalogSelectValue } from './buildDeviceCatalogSelectValue';
type Props = CFormItemProps & {
  /** カタログID。通常表示のroute paramに使用する。 */
  value?: string;
  /** カタログ名。通常表示と編集時inputの表示値に使用する。 */
  displayValue?: string;
  /** 編集時に選択したカタログを返す。undefinedは紐付け解除。 */
  onChange?: (catalog?: DeviceCatalogInfo) => void;
  /** 任意項目で紐付け解除を許可する。 */
  allowEmpty?: boolean;
  /** 候補を同一デバイス種別へ限定するためのジャンル。 */
  deviceGenre?: DeviceGenre;
  readComponent?: ReactNode;
};

/**
 * デバイスカタログ参照属性の共通CFormアイテム。
 * 通常表示は詳細リンク、編集時はActiveかつ同一ジャンルのカタログSelectを描画する。
 */
export const CFormDeviceCatalogSelectItem = buildCFormItem<Props>((props: Props) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.deviceCatalogSelectItem'
  });
  const readView = buildDeviceCatalogReadView(props.value, props.displayValue);
  const selectedCatalog = buildDeviceCatalogSelectValue(props.value, props.displayValue, props.deviceGenre);
  const defaultReadComponent = readView.kind === 'link' ? <Text as="span" variant="p">
          <Link to={readView.to} params={readView.params} className="inline-flex items-center gap-1 text-primary-on-neutral-fill hover:underline focus-visible:underline focus-visible:outline-none">
            <Icon iconName={readView.iconName} className="size-4 shrink-0" inheritColor aria-hidden="true" />
            {readView.label}
          </Link>
        </Text> : readView.kind === 'text' ? <Text as="span" variant="p">
          {readView.label}
        </Text> : <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text>;
  return <CFormItemBase {...props} label={t('label')} readComponent={props.readComponent ?? defaultReadComponent} writeComponent={<DeviceCatalogSelect value={selectedCatalog} disabled={props.readonly} allowEmpty={props.allowEmpty ?? !props.required} genre={props.deviceGenre} requireGenre onChange={catalog => props.onChange?.(catalog)} />} />;
});